import type { ClientGeoArea } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TextInput as RNTextInput } from 'react-native';
import { KeyboardController } from 'react-native-keyboard-controller';

import {
  findNodeById,
  getAllDescendantIds,
  getAncestorIds
} from '@/features/api/hooks/shared-app/useGetGeoAreaTree';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { isEmptyOrNull } from '@/shared/utils/string';

import { buildPickerRoot, expandSyntheticRootIds } from './geoAreaRoot';

/**
 * Shared logic for geo-area selection: modal state, pending selection,
 * tree navigation, and toggle rules (root = whole area, mutually exclusive
 * with sub-areas; deselecting a node clears its descendants).
 *
 * Used by both the outlined form field (`FormGeoAreaSelect`) and the
 * list-item variant (`FormGeoAreaSelectListItem`).
 */
export const useGeoAreaSelection = (treeRoots?: ClientGeoArea[]) => {
  const inputRef = useRef<RNTextInput>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [pendingIds, setPendingIds] = useState<string[]>([]);
  const [navStack, setNavStack] = useState<ClientGeoArea[]>([]);

  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization.geoAreaPicker'
  });
  const rootArea = buildPickerRoot(treeRoots, t('allAreasLabel'));

  const handleOpen = useCallback((initialIds: string[], editable = true) => {
    if (!editable) return;
    inputRef.current?.blur();
    KeyboardController.dismiss();
    setPendingIds(initialIds);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setNavStack([]);
  }, []);

  const handleDone = useCallback(
    (onChange: (ids: string[]) => void) => {
      onChange(expandSyntheticRootIds(pendingIds, treeRoots));
      setIsOpen(false);
      setNavStack([]);
    },
    [pendingIds, treeRoots]
  );

  const currentNode = navStack.at(-1) ?? rootArea;
  const currentAreas = currentNode?.childGeoAreas ?? [];

  const handleDrillDown = useCallback((area: ClientGeoArea) => {
    setNavStack((prev) => [...prev, area]);
  }, []);

  const handleGoBack = useCallback(() => {
    setNavStack((prev) => prev.slice(0, -1));
  }, []);

  const resolveNames = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) {
        return '';
      }
      return ids
        .map((id) => findNodeById(treeRoots, id)?.name)
        .filter((areaName): areaName is string => !isEmptyOrNull(areaName))
        .join(', ');
    },
    [treeRoots]
  );

  const toggle = useCallback(
    (area: ClientGeoArea) => {
      if (pendingIds.includes(area.id)) {
        // Deselecting a node clears its descendants and its ancestors: a
        // parent must not stay selected ("whole area") once one of its
        // sub-areas is removed — it should fall back to indeterminate.
        const removed = new Set([
          ...getAllDescendantIds(area),
          ...getAncestorIds(treeRoots, area.id)
        ]);
        setPendingIds(pendingIds.filter((current) => !removed.has(current)));
        return;
      }

      if (area.id === rootArea?.id) {
        setPendingIds([area.id]);
        return;
      }

      // Selecting a sub-area individually means its ancestors no longer count
      // as "whole area" — drop their ids so they turn indeterminate.
      const ancestors = new Set(getAncestorIds(treeRoots, area.id));
      setPendingIds([
        ...pendingIds.filter((current) => !ancestors.has(current)),
        area.id
      ]);
    },
    [pendingIds, rootArea?.id, treeRoots]
  );

  const getStatus = useCallback(
    (area: ClientGeoArea) => {
      if (pendingIds.includes(area.id)) return 'checked';
      const hasSelectedDescendant = getAllDescendantIds(area).some(
        (id) => id !== area.id && pendingIds.includes(id)
      );
      return hasSelectedDescendant ? 'indeterminate' : 'unchecked';
    },
    [pendingIds]
  );

  const getSelectedDescendantCount = useCallback(
    (area: ClientGeoArea) =>
      getAllDescendantIds(area).filter(
        (id) => id !== area.id && pendingIds.includes(id)
      ).length,
    [pendingIds]
  );

  const allSubAreasSelected =
    currentAreas.length > 0 &&
    currentAreas.every((area) => pendingIds.includes(area.id));

  const handleToggleAll = useCallback(() => {
    if (allSubAreasSelected) {
      const areaIds = new Set(currentAreas.map((a) => a.id));
      setPendingIds(pendingIds.filter((id) => !areaIds.has(id)));
    } else {
      setPendingIds([
        ...pendingIds.filter((id) => id !== rootArea?.id),
        ...currentAreas.map((a) => a.id)
      ]);
    }
  }, [allSubAreasSelected, currentAreas, pendingIds, rootArea?.id]);

  return {
    inputRef,
    isOpen,
    pendingIds,
    navStack,
    treeRoots,
    rootArea,
    currentNode,
    currentAreas,
    allSubAreasSelected,
    handleOpen,
    handleClose,
    handleDone,
    handleDrillDown,
    handleGoBack,
    resolveNames,
    toggle,
    getStatus,
    getSelectedDescendantCount,
    handleToggleAll
  };
};
